
import React, { useState, useRef, useEffect, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { getAdvancedExplanation, ExplanationResponse, Flashcard } from '../services/geminiService.ts';
import { trackEvent } from '../services/analyticsService.ts';
import { getCroppedImg, PixelCrop } from '../utils/imageUtils.ts';

interface Props {
  onSuggestSignup: (reason?: string) => void;
  isPro: boolean;
}

const ExplanationDemo: React.FC<Props> = ({ onSuggestSignup, isPro }) => {
  const [topic, setTopic] = useState('');
  const [ageRange, setAgeRange] = useState('10-12');
  const [image, setImage] = useState<{ data: string; mimeType: string } | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [result, setResult] = useState<ExplanationResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [usageCount, setUsageCount] = useState(0);

  const [tempImageUrl, setTempImageUrl] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<PixelCrop | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const today = new Date().toDateString();
    const stored = JSON.parse(localStorage.getItem('hwb_usage_v3') || '{}');
    if (stored.date === today) {
      setUsageCount(stored.count || 0);
    } else {
      setUsageCount(0);
    }
  }, []);

  const updateUsage = () => {
    const today = new Date().toDateString();
    const newCount = usageCount + 1;
    setUsageCount(newCount);
    localStorage.setItem('hwb_usage_v3', JSON.stringify({ date: today, count: newCount }));
  };

  const onCropComplete = useCallback((_croppedArea: any, pixelCrop: PixelCrop) => {
    setCroppedAreaPixels(pixelCrop);
  }, []);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    trackEvent('image_upload_triggered');
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setTempImageUrl(reader.result as string);
        setShowCropper(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const saveCrop = async () => {
    trackEvent('image_crop_saved');
    if (tempImageUrl && croppedAreaPixels) {
      try {
        const croppedImage = await getCroppedImg(tempImageUrl, croppedAreaPixels);
        setImage(croppedImage);
        setPreviewUrl(`data:${croppedImage.mimeType};base64,${croppedImage.data}`);
        setShowCropper(false);
        setTempImageUrl(null);
      } catch (e) {
        setError('Failed to crop image.');
      }
    }
  };

  const loadExample = () => {
    setTopic("Can you explain how long division works? My 5th grader is struggling with the 'carry over' part.");
    setAgeRange("10-12");
    trackEvent('demo_example_loaded');
  };

  const executeGeneration = async (isRefining: boolean = false) => {
    if (!topic.trim() && !image) return;
    
    if (usageCount >= 3 && !isRefining && !isPro) {
      trackEvent('limit_reached_triggered');
      onSuggestSignup("Daily limit reached. Pro members get unlimited bridges.");
      return;
    }

    setLoading(true);
    setError('');
    if (!isRefining) setResult(null);

    try {
      trackEvent('bridge_generation_started', { ageRange, hasImage: !!image, isRefining });
      const response = await getAdvancedExplanation(topic, ageRange, image || undefined, isRefining);
      setResult(response);
      
      if (!isRefining) updateUsage();
      trackEvent('bridge_generation_success');
    } catch (err: any) {
      setError(err.message);
      trackEvent('bridge_generation_failed', { error: err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="demo-tool" className="w-full max-w-3xl mx-auto p-0 sm:p-4">
      {showCropper && tempImageUrl && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-indigo-950/90 backdrop-blur-md">
          <div className="bg-white w-full max-w-lg rounded-[2.5rem] overflow-hidden shadow-2xl flex flex-col">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center">
              <h4 className="font-bold text-slate-800">Crop Question</h4>
              <button onClick={() => setShowCropper(false)} className="text-slate-400 hover:text-slate-600">✕</button>
            </div>
            <div className="relative h-[300px] bg-slate-100">
              <Cropper image={tempImageUrl} crop={crop} zoom={zoom} aspect={4/3} onCropChange={setCrop} onCropComplete={onCropComplete} onZoomChange={setZoom} />
            </div>
            <div className="p-8 bg-white">
              <button onClick={saveCrop} className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-bold shadow-xl">Confirm Crop</button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-[2.5rem] shadow-2xl border border-slate-100 overflow-hidden">
        <div className="p-8 sm:p-14">
          <div className="flex items-center justify-between mb-10">
            <h3 className="text-2xl font-extrabold text-slate-900 tracking-tight">Try Teaching Bridge</h3>
            <button 
              type="button" 
              onClick={loadExample}
              className="text-xs font-bold text-indigo-600 hover:text-indigo-800 transition-colors underline underline-offset-4"
            >
              Try an Example
            </button>
          </div>
          
          <form onSubmit={(e) => { e.preventDefault(); executeGeneration(false); }} className="space-y-8">
            <div className="flex gap-2 bg-slate-50 p-1.5 rounded-2xl border border-slate-200/60">
              {['7-9', '10-12', '13-14'].map((range) => (
                <button
                  key={range}
                  type="button"
                  onClick={() => { setAgeRange(range); trackEvent('age_range_selected', { range }); }}
                  className={`flex-1 py-3 text-[11px] font-black uppercase tracking-widest rounded-xl transition-all ${
                    ageRange === range ? 'bg-white text-indigo-900 shadow-sm' : 'text-slate-400'
                  }`}
                >
                  Grade {range === '7-9' ? '1-3' : range === '10-12' ? '4-6' : '7-8'}
                </button>
              ))}
            </div>

            <div className="relative group">
              <textarea
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder="Describe the confusing problem or paste the text..."
                className="w-full px-8 py-7 rounded-[2rem] border-2 border-slate-100 focus:border-indigo-200 focus:ring-8 focus:ring-indigo-50 focus:outline-none text-slate-700 transition-all min-h-[160px] resize-none text-base font-medium leading-relaxed"
              />
              <div className="absolute right-5 bottom-5 flex gap-2">
                 <button
                  type="button"
                  onClick={() => { trackEvent('photo_button_clicked'); fileInputRef.current?.click(); }}
                  className={`p-5 rounded-full shadow-lg transition-all active:scale-95 ${image ? 'bg-indigo-50 text-indigo-600' : 'bg-indigo-900 text-white'}`}
                  title="Upload photo"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812-1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </button>
                <input type="file" ref={fileInputRef} onChange={handleImageChange} accept="image/*" className="hidden" />
              </div>
            </div>

            {previewUrl && (
              <div className="flex items-center gap-4 p-4 bg-indigo-50/50 rounded-2xl border border-indigo-100 animate-in slide-in-from-left duration-300">
                <img src={previewUrl} className="w-12 h-12 rounded-xl object-cover shadow-sm" alt="Scanned content" />
                <span className="text-[10px] font-black text-indigo-900 uppercase tracking-widest">Question Scanned</span>
                <button type="button" onClick={() => { setImage(null); setPreviewUrl(null); trackEvent('image_removed'); }} className="ml-auto p-2 text-indigo-300 hover:text-indigo-600">✕</button>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || (!topic.trim() && !image)}
              className="w-full py-6 rounded-3xl font-black text-lg bg-indigo-950 text-white hover:bg-indigo-900 transition-all shadow-xl shadow-indigo-100 disabled:opacity-50 active:scale-95"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-3">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Building Your Bridge...
                </span>
              ) : 'Get Teaching Script'}
            </button>
          </form>

          {error && <div className="mt-8 p-4 bg-red-50 text-red-600 rounded-2xl text-xs font-bold border border-red-100">{error}</div>}
        </div>

        {result && (
          <div className="bg-slate-50/50 p-8 sm:p-14 border-t border-slate-100 space-y-12 animate-in fade-in slide-in-from-bottom-6 duration-500">
            {/* 1. Parent prep section */}
            <div className="space-y-4">
               <h4 className="text-[10px] font-black text-indigo-600 uppercase tracking-widest">Parent Quick-Prep</h4>
               <div className="bg-white p-7 rounded-[2rem] border border-slate-200 text-slate-700 leading-relaxed italic shadow-sm">
                 {result.parent_explanation}
               </div>
            </div>

            {/* 2. Main Teaching Script */}
            <div className="bg-indigo-900 text-indigo-50 p-10 rounded-[2.5rem] shadow-2xl relative overflow-hidden group">
               <div className="absolute inset-0 bg-gradient-to-br from-indigo-800 to-indigo-950 opacity-0 group-hover:opacity-10 transition-opacity"></div>
               <span className="text-[10px] font-black uppercase opacity-60 block mb-5 tracking-widest">Teacher Script (Read aloud)</span>
               <p className="text-xl sm:text-2xl font-bold leading-relaxed">"{result.child_explanation}"</p>
            </div>

            {/* 3. Steps and tips */}
            <div className="grid md:grid-cols-2 gap-10">
               <div className="space-y-5">
                  <h5 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">The Teaching Path</h5>
                  <div className="space-y-3">
                    {result.steps.map((s, i) => (
                      <div key={i} className="flex gap-5 p-5 bg-white rounded-2xl border border-slate-100 shadow-sm transition-transform hover:translate-x-1">
                         <span className="font-black text-indigo-600 text-sm">0{i+1}</span>
                         <p className="text-xs font-semibold text-slate-600 leading-relaxed">{s}</p>
                      </div>
                    ))}
                  </div>
               </div>
               <div className="space-y-5">
                  <h5 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Parent Secrets</h5>
                  <div className="space-y-3">
                    {result.teaching_tips.map((t, i) => (
                      <div key={i} className="p-5 bg-indigo-50/50 text-indigo-900 rounded-2xl text-[11px] font-bold italic leading-relaxed border border-indigo-100/50">
                         " {t} "
                      </div>
                    ))}
                  </div>
               </div>
            </div>

            {/* 4. Flashcards Section - Final recap for child */}
            <div className="space-y-6 pt-10 border-t border-slate-200">
              <h4 className="text-[10px] font-black text-indigo-600 uppercase tracking-widest">Interactive Flashcards</h4>
              <div className="flex gap-4 overflow-x-auto pb-4 -mx-8 px-8 snap-x no-scrollbar">
                {result.flashcards.map((card, i) => (
                  <div key={i} className="min-w-[240px] bg-white p-8 rounded-[2.5rem] border-2 border-indigo-100 shadow-sm snap-center hover:border-indigo-400 transition-all group">
                    <div className="text-4xl mb-5 group-hover:scale-110 transition-transform">{card.icon_suggestion}</div>
                    <h5 className="text-base font-black text-slate-900 mb-3">{card.term}</h5>
                    <p className="text-xs text-slate-500 leading-relaxed font-medium mb-6">{card.definition}</p>
                    <div className="text-[9px] font-black text-indigo-600 uppercase tracking-[0.2em] opacity-40">Card {i+1} of {result.flashcards.length}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="pt-6 border-t border-slate-100">
              <button 
                onClick={() => executeGeneration(true)}
                className="text-xs font-bold text-slate-400 hover:text-indigo-600 transition-colors uppercase tracking-widest"
              >
                Still confusing? Build an even simpler bridge →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExplanationDemo;
